import React, { useEffect, useState } from "react";
import {
  UncontrolledDropdown,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import { Modal } from "antd";
import { ReactTyped } from "react-typed";
import { FormProvider, useForm } from "react-hook-form";
import sitebreakdownMasterApis from "../../../../api/master/sitebreakdownMaster";
import { Icon } from "../../../../components/Component";
import FormInput from "../../../form-components/InputComponent";
import { exportToXLSX } from "../../../../utils/Utils";

interface SelectOptions {
  label: string;
  value: any;
}

const blobUrl: String = process.env.REACT_APP_BLOB_URL || "";
const blobContainer: String = process.env.REACT_APP_BLOB_CONTAINER || "";

const SiteStructureBulkUpload = ({
  onUpload,
  projectType,
  siteStartDate,
  siteEndDate,
}: {
  onUpload: (structures: any[]) => void;
  projectType: string;
  siteStartDate?: string;
  siteEndDate?: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [blockEdit, setBlockEdit] = useState(false);
  const [response, setResponse] = useState({
    status: "",
    message: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);

  const methods = useForm({
    mode: "onChange",
  });

  const { watch, setValue } = methods;
  const files = watch("attachment");

  useEffect(() => {
    setResponse({ status: "", message: "" });
  }, [files]);

  const fetchUOMOPtions = async () => {
    try {
      const response = await sitebreakdownMasterApis.workUom({});
      const { data, status } = response || {};
      if (status) {
        setUomOptions(
          data?.map((item: any) => ({
            label: item.uomName,
            value: item.workUomId,
          })) || []
        );
      }
    } catch (error) {
      console.error("Error fetching work UOMs:", error);
    }
  };

  useEffect(() => {
    fetchUOMOPtions();
  }, []);

  // Template URL logic
  const getTemplateUrl = () => {
    const baseUrl = `${blobUrl}${blobContainer}/Master/`;
    switch (projectType) {
      case "1":
        return `${baseUrl}SiteStructureLines-Villas.xlsx`;
      case "2":
        return `${baseUrl}SiteStructureLines-Highrise.xlsx`;
      case "3":
        return `${baseUrl}SiteStructureLines-StandAlone.xlsx`;
      case "4":
        return `${baseUrl}SiteStructureLines-Commercial.xlsx`;
      case "5":
        return `${baseUrl}SiteStructureLines-Infra.xlsx`;
      default:
        return `${baseUrl}SiteStructureLines-Template.xlsx`;
    }
  };

  const getProjectTypeName = () => {
    switch (projectType) {
      case "1":
        return "Villas";
      case "2":
        return "Highrise";
      case "3":
        return "Standalone";
      case "4":
        return "Commercial";
      case "5":
        return "Infrastructure";
      default:
        return "Site Structure";
    }
  };

  const handleUpload = async () => {
    try {
      const attachment = files;
      if (!attachment || !attachment[0]) {
        setResponse({
          status: "error",
          message: "Please select a file",
        });
        setBlockEdit(false);
        setLoading(false);
        return;
      }

      await uploadFile(attachment, setResponse, "File has been uploaded");
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const uploadFile = async (
    attachment: any,
    setResponse: any,
    successResponse: string
  ) => {
    try {
      const _file = attachment?.[0];
      const formData: any = new FormData();
      formData.append("file", _file);
      formData.append("projectType", projectType);
      if (siteStartDate) formData.append("startDate", siteStartDate);
      if (siteEndDate) formData.append("endDate", siteEndDate);

      const res: any =
        await sitebreakdownMasterApis.siteStructureBulkUploadValidate(formData);

      if (res?.status) {
        setResponse({
          status: "success",
          message: successResponse,
        });

        const apiData = res?.data || res?.response || {};
        const allUnits = apiData?.allUnits || [];

        // Transform response into table-ready data
        const processedUnits = allUnits.map((unit: any, idx: number) => ({
          key: `unit_${idx}_${Date.now()}`,
          unitName: unit.unitName || "",
          unitQty: unit.unitQty || 0,
          unitUomId: unit.unitUomId || "",
          uomName: unit.uomName || "",
          uomShortCode: unit.uomShortCode || "",
          description: unit.description || "",
          plannedStartDate: unit.plannedStartDate || "",
          plannedEndDate: unit.plannedEndDate || "",
          isChildExist: unit.isChildExist || false,
          noOfFloors: unit.noOfFloors || "",
          unitPerFloor: unit.unitPerFloor || "",
          noOfUnitsPerFloor: unit.noOfUnitsPerFloor || "",
          subUnits:
            Array.isArray(unit.subUnits) && unit.subUnits.length > 0
              ? unit.subUnits.map((s: any, i: number) => ({
                  key: `sub_${idx}_${i}_${Date.now()}`,
                  subUnitName: s.subUnitName || "",
                  floorNo: s.floorNo || "",
                  floorType: s.floorType || "",
                  description: s.description || "",
                  plannedStartDate: s.plannedStartDate || "",
                  plannedEndDate: s.plannedEndDate || "",
                  unit: s.unit || "",
                }))
              : [],
        }));

        // Pass processed data to parent
        onUpload(processedUnits);

        // Reset modal state
        setTimeout(() => {
          setShowModal(false);
          setBlockEdit(false);
          setValue("attachment", null);
          setLoading(false);
          setResponse({ status: "", message: "" });
        }, 600);
      } else {
        setResponse({
          status: "error",
          message: res?.message || "Upload failed",
        });
        setBlockEdit(false);
        setLoading(false);
      }
    } catch (error) {
      console.error("Upload file error:", error);
      setResponse({
        status: "error",
        message: "An error occurred while uploading the file",
      });
      setBlockEdit(false);
      setLoading(false);
    }
  };

  return (
    <>
      <UncontrolledDropdown size="md">
        <div
          onClick={() => setShowModal(true)}
          className="btn btn-white btn-outline-light"
        >
          <Icon name="upload-cloud" />
          <span>Upload Site Structure</span>
        </div>
      </UncontrolledDropdown>

      <Modal
        title={`Bulk ${getProjectTypeName()} Structure Upload`}
        open={showModal}
        footer={null}
        onCancel={() => {
          setShowModal(false);
          setBlockEdit(false);
          setValue("attachment", null);
          setLoading(false);
          setResponse({ status: "", message: "" });
        }}
        width="60%"
      >
        <FormProvider {...methods}>
          <form>
            <p>
              You're about to upload multiple{" "}
              {getProjectTypeName().toLowerCase()} structures at once. Please{" "}
              <a
                href={getTemplateUrl()}
                target="_self"
                style={{ textDecoration: "underline" }}
              >
                Download {getProjectTypeName()} Template
              </a>{" "}
              and fill it carefully before uploading below.You can check{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  exportToXLSX(
                    uomOptions.map((uom) => ({
                      uom_name: uom.label,
                      id: uom.value,
                    })),
                    "UOMs"
                  );
                }}
                style={{ textDecoration: "underline" }}
              >
                UOMs
              </a>{" "}
              for reference.
            </p>

            <Col md={4}>
              {files && files[0]?.name ? (
                <div className="form-group m-0">
                  <label className="form-label">File Upload Area</label>
                  <div className="form-control-wrap d-flex align-items-center justify-content-between border p-1">
                    <span>{files[0]?.name}</span>
                    {!blockEdit && (
                      <span
                        onClick={() => setValue("attachment", null)}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross" /> &nbsp;
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <FormInput
                  type="file"
                  name="attachment"
                  label="File Upload Area"
                  placeholder="File Upload Area"
                  accept=".xlsx,.csv"
                />
              )}
            </Col>

            <Alert
              isOpen={response.status === "error"}
              color="danger"
              className="w-100 py-1 mt-2"
            >
              <ReactTyped
                strings={[response.message]}
                typeSpeed={30}
                showCursor={false}
              />
            </Alert>

            <Alert
              isOpen={response.status === "success"}
              color="success"
              className="w-100 py-1 mt-2"
            >
              <p>
                {response.message}, You can{" "}
                <span
                  className="inlineTableCellLink"
                  onClick={() => {
                    setShowModal(false);
                    setValue("attachment", null);
                    setResponse({ status: "", message: "" });
                  }}
                >
                  close
                </span>{" "}
                this popup.
              </p>
            </Alert>

            <Row className="mt-4">
              <Col>
                {response.status === "success" ? null : (
                  <ul className="actions clearfix d-flex">
                    <li>
                      {loading ? (
                        <Button color="primary" type="button" size="md">
                          <Spinner size="sm" color="light" />
                        </Button>
                      ) : (
                        <Button
                          color="primary"
                          type="button"
                          disabled={!files?.[0]}
                          onClick={() => {
                            setResponse({ status: "", message: "" });
                            setBlockEdit(true);
                            setLoading(true);
                            handleUpload();
                          }}
                        >
                          Upload
                        </Button>
                      )}
                    </li>
                    {!loading && (
                      <li className="m-3 mt-0">
                        <Button
                          color="secondary"
                          onClick={() => {
                            setShowModal(false);
                            setValue("attachment", null);
                          }}
                          className="btn-dim"
                        >
                          Cancel
                        </Button>
                      </li>
                    )}
                  </ul>
                )}
              </Col>
            </Row>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default SiteStructureBulkUpload;
